import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Notice, Spinner } from '../../components/ui';
import { Dialog } from '../../components/Dialog';
import { SearchAdd, type SearchAddItem } from '../../components/SearchAdd';
import { BackLink } from '../../components/BackLink';
import { Pager } from '../../components/Pager';
import { api, ApiError } from '../../lib/api';
import { getFramework, grantAccess, revokeAccess } from '../../lib/frameworks';
import '../Page.css';
import '../../components/Form.css';
import './Admin.css';

export type OrgRow = { schoolId: string; name: string; inviteCode: string | null; isDefault: boolean };
type Note = { tone: 'success' | 'error'; text: string } | null;

const errorText = (err: unknown) => (err instanceof ApiError ? err.message : 'That didn’t work. Try again.');

/**
 * Organisations to add: searched on the server as you type (so it copes with
 * hundreds), leaving out those that already have the framework.
 */
export async function searchOrgsToAdd(frameworkId: string, q: string, exclude: Set<string>): Promise<SearchAddItem[]> {
  const res = await api<{ data: { id: string; name: string; invite_code: string | null }[] }>(
    `/admin/schools?q=${encodeURIComponent(q)}&page=1&pageSize=12&without=${encodeURIComponent(frameworkId)}`,
  );
  // `exclude` also covers anyone added a moment ago, before the list refreshes.
  return res.data.filter((o) => !exclude.has(o.id)).map((o) => ({ id: o.id, title: o.name, sub: o.invite_code }));
}

/** Search-and-click to give an organisation this framework. */
export function AddOrg({ frameworkId, exclude, onDone }: { frameworkId: string; exclude: Set<string>; onDone: (note: Note) => void }) {
  return (
    <SearchAdd
      label="Add an organisation"
      placeholder="Search by name or code"
      noun="organisation"
      load={(q) => searchOrgsToAdd(frameworkId, q, exclude)}
      onAdd={async (o) => {
        try {
          const r = await grantAccess(frameworkId, o.id, 'ifNone');
          onDone({
            tone: 'success',
            text: r.setAsDefault ? `${o.title} added. It’s their default.` : `${o.title} added. They already had a default, so that hasn’t changed.`,
          });
        } catch (err) {
          onDone({ tone: 'error', text: errorText(err) });
        }
      }}
    />
  );
}

/** The organisations with a framework: make it their default, or take it away. */
export function OrgList({
  frameworkId,
  ownerSchoolId,
  rows,
  onDone,
}: {
  frameworkId: string;
  ownerSchoolId: string | null;
  rows: OrgRow[];
  onDone: (note: Note) => void;
}) {
  const [removing, setRemoving] = useState<OrgRow | null>(null);
  const act = async (fn: () => Promise<unknown>, text: string) => {
    try {
      await fn();
      onDone({ tone: 'success', text });
    } catch (err) {
      onDone({ tone: 'error', text: errorText(err) });
    }
  };
  const remove = (s: OrgRow) => act(() => revokeAccess(frameworkId, s.schoolId), `${s.name} no longer has it.`);

  return (
    <>
      <div className="list">
        {rows.map((s) => (
          <div key={s.schoolId} className="list-row fw-org-row">
            <div className="list-main">
              <p className="list-title">{s.name}</p>
              <p className="list-sub">{s.schoolId === ownerSchoolId ? `${s.inviteCode ?? ''} · made for them` : s.inviteCode}</p>
            </div>
            <div className="fw-org-actions">
              {s.isDefault ? (
                <span className="chip chip-accent">Their default</span>
              ) : (
                <Button variant="ghost" onClick={() => act(() => grantAccess(frameworkId, s.schoolId, true), `It’s now ${s.name}’s default.`)}>
                  Make default
                </Button>
              )}
              {s.schoolId === ownerSchoolId ? null : (
                <Button variant="ghost" onClick={() => (s.isDefault ? setRemoving(s) : remove(s))}>
                  Remove
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!removing} onClose={() => setRemoving(null)} title={`Remove ${removing?.name ?? ''}?`}>
        <p className="muted">It&rsquo;s their default. Their new lessons will be scored against the standard EducAitor framework until you give them another.</p>
        <div className="dialog-actions">
          <Button variant="ghost" onClick={() => setRemoving(null)}>
            Keep it
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              const r = removing;
              setRemoving(null);
              if (r) remove(r);
            }}
          >
            Remove
          </Button>
        </div>
      </Dialog>
    </>
  );
}

const PAGE_SIZE = 25;

/** Every organisation with a framework: search, add, remove, make default, a page at a time. */
export function AdminFrameworkOrgsPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const fw = useQuery({ queryKey: ['admin-framework', id], queryFn: () => getFramework(id) });
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [note, setNote] = useState<Note>(null);
  useEffect(() => setPage(1), [q]);
  const orgs = useQuery({
    queryKey: ['admin-fw-orgs', id, q, page],
    queryFn: () =>
      api<{ data: OrgRow[]; total: number }>(`/admin/frameworks/${id}/organisations?q=${encodeURIComponent(q)}&page=${page}&pageSize=${PAGE_SIZE}`),
    placeholderData: (prev) => prev,
  });

  const done = async (n: Note) => {
    setNote(n);
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['admin-fw-orgs', id] }),
      qc.invalidateQueries({ queryKey: ['admin-framework', id] }),
      qc.invalidateQueries({ queryKey: ['admin-frameworks'] }),
    ]);
  };

  if (fw.isLoading) return <Spinner />;
  if (fw.isError || !fw.data) return <Notice tone="error">This framework isn&rsquo;t available.</Notice>;
  const f = fw.data;
  const rows = orgs.data?.data ?? [];
  const total = orgs.data?.total ?? 0;
  // Everyone who has it, for leaving them out of "Add" (the editor's list is complete).
  const has = new Set(f.sharedWithSchools.map((s) => s.schoolId));

  return (
    <div className="page">
      <BackLink to={`/admin/frameworks/${id}`}>{f.name}</BackLink>
      <header className="page-head">
        <p className="eyebrow">{f.name}</p>
        <h1>Organisations</h1>
        <p className="muted">Who can use this framework. Their default is what new lessons are scored against.</p>
      </header>

      {f.status === 'approved' ? (
        <section className="panel form-section">
          <AddOrg frameworkId={id} exclude={has} onDone={done} />
        </section>
      ) : (
        <Notice>Publish the framework before giving organisations access.</Notice>
      )}

      <div aria-live="polite">{note ? <Notice tone={note.tone}>{note.text}</Notice> : null}</div>

      <section className="panel form-section">
        <div className="list-head">
          <h2>
            {total} {total === 1 ? 'organisation' : 'organisations'}
            {q ? <span className="faint"> matching “{q}”</span> : null}
          </h2>
          <label className="filter">
            <span className="sr-label">Find an organisation with this framework</span>
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
              <path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find in this list" />
          </label>
        </div>
        {orgs.isLoading ? (
          <Spinner />
        ) : orgs.isError ? (
          <Notice tone="error">Couldn&rsquo;t load organisations.</Notice>
        ) : rows.length === 0 ? (
          <p className="muted">{q ? 'No organisation with this framework matches that.' : 'No organisations have it yet.'}</p>
        ) : (
          <OrgList frameworkId={id} ownerSchoolId={f.school_id} rows={rows} onDone={done} />
        )}
      </section>
      <Pager page={page} pageSize={PAGE_SIZE} total={total} onPage={setPage} noun="organisations" />
    </div>
  );
}
